import { makeAutoObservable } from "mobx";
import {
  AddArticle,
  ArticlesOfAssociationChangeMode,
  DbotEvent,
  DecisionContext,
  EditArticle,
  RemoveArticle,
} from "../common/commonDomain";
import { Language } from "../../i18n/languages";
import { noDialog } from "../summary/summaryDomain";
import { compact, identity } from "lodash";
import { ArticlesOfAssociation, IArticle } from "../../types/ArticlesOfAssociation";
import { newUuid } from "../../utils/uuid";
import { TMessage } from "../../i18n/tt";
import { DecisionType } from "../../common/decisions";
import { ArticleChangeType } from "../../common/common";
import {
  ArticleSelectorModel,
  initArticleTextModels,
  initArticleTitleModels,
  LocalizedStringFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "../../common/commonForms";

export type ChangeArticlesOfAssociation = {
  type: DecisionType.changeArticlesOfAssociation;
  articleChanges: ArticlesOfAssociationChangeMode[];
};

export type ArticleChangeDialogState =
  | { type: "none" }
  | { type: ArticleChangeType.AddArticle; model: AddArticleModel }
  | { type: ArticleChangeType.RemoveArticle; model: RemoveArticleModel }
  | { type: ArticleChangeType.EditArticle; model: EditArticleModel }
  | {
      type: "delete";
      data: ArticlesOfAssociationChangeMode;
      onSubmit: (data: ArticlesOfAssociationChangeMode) => void;
      onCancel: () => void;
    };

export class ChangeArticlesOfAssociationForm {
  readonly type = DecisionType.changeArticlesOfAssociation;
  data?: ChangeArticlesOfAssociation;
  context: DecisionContext;

  articleChangeModel: ChangeArticlesOfAssociationModel;

  constructor(context: DecisionContext, data?: ChangeArticlesOfAssociation) {
    makeAutoObservable(this, { type: false, data: false }, { autoBind: true });
    this.data = data;
    this.context = context;

    this.articleChangeModel = new ChangeArticlesOfAssociationModel(
      this.context,
      data?.articleChanges,
    );
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/update-input-state": {
        this.articleChangeModel.updateArticles(this.context.inputState?.articles);
        break;
      }
    }
  }

  validate(): boolean {
    this.articleChangeModel.validate();
    return this.isValid;
  }

  get isValid(): boolean {
    return this.articleChangeModel.isValid;
  }
}

export class ChangeArticlesOfAssociationModel {
  articleChanges: ArticlesOfAssociationChangeMode[];
  articles: ArticleSelectorModel;

  dialogState: ArticleChangeDialogState;
  error?: TMessage = undefined;

  constructor(
    readonly context: DecisionContext,
    data?: ArticlesOfAssociationChangeMode[],
  ) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.articleChanges = data ?? [];
    this.articles = new ArticleSelectorModel(context.inputState?.articles);

    this.dialogState = noDialog();
  }

  updateArticles(articlesOfAssociation?: ArticlesOfAssociation) {
    this.articles = new ArticleSelectorModel(articlesOfAssociation);
  }

  get articlesAfterChanges() {
    if (!this.context.inputState?.articles) {
      return;
    }
    let newArticles = Object.assign({}, this.context.inputState?.articles);
    this.articleChanges.forEach((articleChange) => {
      newArticles = changeArticles(articleChange, newArticles);
    });
    return newArticles;
  }

  get defaultLanguages() {
    return this.context.inputState?.articles?.languages ?? [Language.NO];
  }

  getArticleTitleBeforeChange(data: EditArticle | RemoveArticle, language: Language) {
    return this.defaultLanguages.includes(language)
      ? (data.titlesBefore.find((d) => d.language === language)?.text ?? "")
      : (data.titlesBefore.find((d) => d.language === this.defaultLanguages[0])?.text ?? "");
  }

  getArticleTextBeforeChange(data: EditArticle | RemoveArticle, language: Language) {
    return this.defaultLanguages.includes(language)
      ? (data.textsBefore.find((d) => d.language === language)?.text ?? "")
      : (data.textsBefore.find((d) => d.language === this.defaultLanguages[0])?.text ?? "");
  }

  getArticleTitleAfterChange(data: EditArticle | AddArticle, language: Language) {
    return this.defaultLanguages.includes(language)
      ? (data.titlesAfter.find((d) => d.language === language)?.text ?? "")
      : (data.titlesAfter.find((d) => d.language === this.defaultLanguages[0])?.text ?? "");
  }

  getArticleTextAfterChange(data: EditArticle | AddArticle, language: Language) {
    return this.defaultLanguages.includes(language)
      ? (data.textsAfter.find((d) => d.language === language)?.text ?? "")
      : (data.textsAfter.find((d) => d.language === this.defaultLanguages[0])?.text ?? "");
  }

  showArticleChangeDialog(type: ArticleChangeType, data?: ArticlesOfAssociationChangeMode) {
    switch (type) {
      case "AddArticle":
        this.dialogState = {
          type: type,
          model: new AddArticleModel(
            this.closeDialog,
            this.submitDialog,
            this.articlesAfterChanges,
            data?.type === type ? data : undefined,
          ),
        };
        break;
      case "RemoveArticle":
        this.dialogState = {
          type: type,
          model: new RemoveArticleModel(
            this.closeDialog,
            this.submitDialog,
            this.articlesAfterChanges,
            data?.type === type ? data : undefined,
          ),
        };
        break;
      case "EditArticle":
        this.dialogState = {
          type: type,
          model: new EditArticleModel(
            this.closeDialog,
            this.submitDialog,
            this.articlesAfterChanges,
            data?.type === type ? data : undefined,
          ),
        };
        break;
    }
  }

  submitDialog(data: ArticlesOfAssociationChangeMode) {
    const index = this.articleChanges.findIndex((x) => x.id === data.id);
    if (index !== -1) {
      this.articleChanges = [
        ...this.articleChanges.slice(0, index),
        data,
        ...this.articleChanges.slice(index + 1),
      ];
    } else {
      this.articleChanges = [...this.articleChanges, data];
    }
    this.validate();
    this.closeDialog();
  }

  showDeleteArticleDialog(data: ArticlesOfAssociationChangeMode) {
    this.dialogState = {
      type: "delete",
      data: data,
      onSubmit: this.deleteArticleChange,
      onCancel: this.closeDialog,
    };
  }

  deleteArticleChange(data: ArticlesOfAssociationChangeMode) {
    this.articleChanges = updateArticleChangesOnDelete(data, this.articleChanges);
    this.closeDialog();
  }

  closeDialog() {
    this.dialogState = noDialog();
  }

  validate(): boolean {
    if (this.articleChanges.length === 0) {
      this.error = { key: "required" };
    } else {
      this.error = undefined;
    }
    return this.isValid;
  }

  get isValid(): boolean {
    return !this.error;
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }
}

export class RemoveArticleModel {
  type: "RemoveArticle";
  id: string;
  paragraph: ArticleSelectorModel;
  isEdit: boolean;
  closeDialog: () => void;
  submitDialog: (data: RemoveArticle) => void;
  articles?: ArticlesOfAssociation;

  constructor(
    closeDialog: () => void,
    submitDialog: (data: RemoveArticle) => void,
    articles?: ArticlesOfAssociation,
    data?: RemoveArticle,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.articles = articles;
    this.type = "RemoveArticle";
    this.id = data?.id ?? newUuid();
    this.paragraph = new ArticleSelectorModel(articles, data?.paragraph);

    this.isEdit = data !== undefined;
    this.closeDialog = closeDialog;
    this.submitDialog = submitDialog;
  }

  close() {
    this.closeDialog();
  }
  submit() {
    if (this.validate()) {
      this.submitDialog(this.toRemoveArticle());
    }
  }

  get canSubmit() {
    return this.isValid;
  }

  get selectedArticles() {
    return this.articles?.jsondata.filter((d) => d.paragraph === this.paragraph.selected) ?? [];
  }

  toRemoveArticle(): RemoveArticle {
    return {
      type: ArticleChangeType.RemoveArticle,
      id: this.id,
      paragraph: this.paragraph.selected ?? 0,
      titlesBefore: this.selectedArticles.map((m) => ({
        text: m.name,
        language: m.language,
      })),
      textsBefore: this.selectedArticles.map((m) => ({
        text: m.text,
        language: m.language,
      })),
    };
  }

  validate(): boolean {
    return this.isValid;
  }

  get isValid(): boolean {
    return this.paragraph.isValid;
  }
}

export class AddArticleModel {
  type: "AddArticle";
  articles?: ArticlesOfAssociation;
  id: string;
  paragraph: RadioFieldModel<number>;
  languages: Language[];
  titles: LocalizedStringFieldModel[];
  texts: LocalizedStringFieldModel[];
  isEdit: boolean;
  closeDialog: () => void;
  submitDialog: (data: AddArticle) => void;

  constructor(
    closeDialog: () => void,
    submitDialog: (data: AddArticle) => void,
    articles?: ArticlesOfAssociation,
    data?: AddArticle,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.type = "AddArticle";
    this.id = data?.id ?? newUuid();
    this.articles = articles;
    this.paragraph = new RadioFieldModel<number>({
      value: data?.paragraph ?? 1,
      options: [...this.articlesByParagraph.keys(), this.articlesByParagraph.size + 1],
      idFn: identity,
      labelFn: (option) => ({ translated: "§ " + option }),
    });
    this.languages = articles?.languages ?? [Language.NO];
    this.texts = this.languages.map(
      (l) =>
        new LocalizedStringFieldModel(
          new StringFieldModel({
            value: data?.textsAfter?.find((t) => t.language === l)?.text,
            required: true,
          }),
          l,
        ),
    );
    this.titles = this.languages.map(
      (l) =>
        new LocalizedStringFieldModel(
          new StringFieldModel({
            value: data?.titlesAfter?.find((t) => t.language === l)?.text,
          }),
          l,
        ),
    );

    this.isEdit = data !== undefined;
    this.closeDialog = closeDialog;
    this.submitDialog = submitDialog;
  }

  get articlesByParagraph(): Map<number, Map<Language, IArticle>> {
    return (this.articles?.jsondata ?? []).reduce((m, a) => {
      if (!m.has(a.paragraph)) {
        m.set(a.paragraph, new Map());
      }
      m.get(a.paragraph)!.set(a.language, a);
      return m;
    }, new Map<number, Map<Language, IArticle>>());
  }

  close() {
    this.closeDialog();
  }
  submit() {
    if (this.validate()) {
      this.submitDialog(this.toAddArticle());
    }
  }

  get canSubmit() {
    return this.isValid;
  }

  toAddArticle(): AddArticle {
    return {
      type: ArticleChangeType.AddArticle,
      id: this.id,
      paragraph: this.paragraph.value,
      titlesAfter: this.titles.map((m) => ({
        text: m.model.validatedValue,
        language: m.locale,
      })),
      textsAfter: this.texts.map((m) => ({
        text: m.model.validatedValue,
        language: m.locale,
      })),
    };
  }

  validate(): boolean {
    for (const t of this.titles.values()) {
      t.model.validate();
    }
    for (const t of this.texts.values()) {
      t.model.validate();
    }
    return this.isValid;
  }

  get isValid(): boolean {
    return (
      Array.from(this.texts.values()).every((t) => t.model.isValid) &&
      Array.from(this.titles.values()).every((t) => t.model.isValid) &&
      this.paragraph.isValid
    );
  }
}

export class EditArticleModel {
  type: "EditArticle";
  id: string;
  languages: Language[];
  paragraph: ArticleSelectorModel;
  titles: LocalizedStringFieldModel[];
  texts: LocalizedStringFieldModel[];

  isEdit: boolean;
  closeDialog: () => void;
  submitDialog: (data: EditArticle) => void;
  articles?: ArticlesOfAssociation;

  constructor(
    closeDialog: () => void,
    submitDialog: (data: EditArticle) => void,
    articles?: ArticlesOfAssociation,
    data?: EditArticle,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.type = "EditArticle";
    this.id = data?.id ?? newUuid();
    this.languages = articles?.languages ?? [Language.NO];
    this.paragraph = new ArticleSelectorModel(articles, data?.paragraph);
    this.titles = initArticleTitleModels({
      selectorModel: this.paragraph,
      value: data?.titlesAfter,
    });
    this.texts = initArticleTextModels({ selectorModel: this.paragraph, value: data?.textsAfter });

    this.isEdit = data !== undefined;
    this.closeDialog = closeDialog;
    this.submitDialog = submitDialog;
    this.articles = articles;
  }

  close() {
    this.closeDialog();
  }
  submit() {
    if (this.validate()) {
      this.submitDialog(this.toEditArticle());
    }
  }

  get canSubmit() {
    return this.isValid;
  }

  setParagraphValue(newParagraph: number | undefined) {
    this.paragraph.setSelected(newParagraph);
    this.titles = initArticleTitleModels({ selectorModel: this.paragraph });
    this.texts = initArticleTextModels({ selectorModel: this.paragraph });
  }

  get selectedArticles() {
    return this.articles?.jsondata.filter((d) => d.paragraph === this.paragraph.selected) ?? [];
  }

  toEditArticle(): EditArticle {
    return {
      type: ArticleChangeType.EditArticle,
      id: this.id,
      paragraph: this.paragraph.selected ?? 1,
      titlesBefore: this.selectedArticles.map((m) => ({
        text: m.name,
        language: m.language,
      })),
      textsBefore: this.selectedArticles.map((m) => ({
        text: m.text,
        language: m.language,
      })),
      titlesAfter: this.titles.map((m) => ({
        text: m.model.validatedValue,
        language: m.locale,
      })),
      textsAfter: this.texts.map((m) => ({
        text: m.model.validatedValue,
        language: m.locale,
      })),
    };
  }

  validate(): boolean {
    for (const t of this.titles.values()) {
      t.model.validate();
    }
    for (const t of this.texts.values()) {
      t.model.validate();
    }
    return this.isValid;
  }

  get isValid(): boolean {
    return (
      Array.from(this.texts.values()).every((t) => t.model.isValid) &&
      Array.from(this.titles.values()).every((t) => t.model.isValid) &&
      this.paragraph.isValid
    );
  }
}

export const articleChangeCaptions: { [key in ArticleChangeType]: TMessage } = {
  [ArticleChangeType.AddArticle]: { key: "decisions.changeArticlesOfAssociation.addArticle" },
  [ArticleChangeType.EditArticle]: { key: "decisions.changeArticlesOfAssociation.editArticle" },
  [ArticleChangeType.RemoveArticle]: { key: "decisions.changeArticlesOfAssociation.removeArticle" },
};

export function changeArticles(
  articleChange: ArticlesOfAssociationChangeMode,
  articles: ArticlesOfAssociation,
): ArticlesOfAssociation {
  switch (articleChange.type) {
    case ArticleChangeType.EditArticle:
      return updateArticle(articleChange, articles);
    case ArticleChangeType.AddArticle:
      return insertArticle(articleChange, articles);
    case ArticleChangeType.RemoveArticle:
      return deleteArticle(articleChange, articles);
  }
}

function insertArticle(model: AddArticle, articles: ArticlesOfAssociation): ArticlesOfAssociation {
  const newArticles = articles.languages.map((l) => {
    const text = model.textsAfter.find((t) => t.language === l);
    const title = model.titlesAfter.find((t) => t.language === l);
    return {
      name: title?.text || "",
      paragraph: model.paragraph,
      text: text?.text || "",
      language: l,
    };
  });

  const currentArticles = articles.jsondata.map((article) => {
    if (article.paragraph >= model.paragraph) {
      return { ...article, paragraph: article.paragraph + 1 };
    }
    return article;
  });

  articles.jsondata = [...currentArticles, ...newArticles].sort(
    (a, b) => a.paragraph - b.paragraph,
  );
  return articles;
}

function updateArticle(model: EditArticle, articles: ArticlesOfAssociation): ArticlesOfAssociation {
  articles.jsondata = articles.jsondata.map((article) => {
    if (article.paragraph === model.paragraph) {
      return {
        ...article,
        name: model.titlesAfter.find((t) => t.language === article.language)?.text || article.name,
        text: model.textsAfter.find((t) => t.language === article.language)?.text || article.text,
      };
    }
    return article;
  });
  return articles;
}

function deleteArticle(
  model: RemoveArticle,
  articles: ArticlesOfAssociation,
): ArticlesOfAssociation {
  const newArticles: IArticle[] = [];
  const iterate = articles.jsondata[Symbol.iterator]();
  let result = iterate.next();
  while (!result.done) {
    const article = result.value;
    if (article.paragraph > model.paragraph) {
      newArticles.push({ ...article, paragraph: article.paragraph - 1 });
    } else if (article.paragraph !== model.paragraph) {
      newArticles.push(article);
    }
    result = iterate.next();
  }
  articles.jsondata = newArticles.sort((a, b) => a.paragraph - b.paragraph);
  return articles;
}

export function updateArticleChangesOnDelete(
  data: ArticlesOfAssociationChangeMode,
  articleChanges: ArticlesOfAssociationChangeMode[],
): ArticlesOfAssociationChangeMode[] {
  const index = articleChanges.findIndex((x) => x.id === data.id);
  if (index !== -1) {
    switch (data.type) {
      case ArticleChangeType.AddArticle:
        return compact(
          articleChanges.map((article, newIndex) =>
            newIndex === index
              ? undefined
              : newIndex > index && article.paragraph > data.paragraph
                ? { ...article, paragraph: article.paragraph - 1 }
                : article,
          ),
        );
      case ArticleChangeType.RemoveArticle:
        return compact(
          articleChanges.map((article, newIndex) =>
            newIndex === index
              ? undefined
              : newIndex > index && article.paragraph > data.paragraph
                ? { ...article, paragraph: article.paragraph + 1 }
                : article,
          ),
        );
      case ArticleChangeType.EditArticle:
        return articleChanges.filter((d) => d.id !== data.id);
    }
  } else return articleChanges;
}
